/**
 * Bookings panel — lists every itinerary item. Non-hotel items group by type;
 * hotel rooms render one row each, grouped into per-stay sections. The heavy
 * lifting lives in `./bookings/` (with the platform-neutral parts in
 * `@elara/client-core/lib/bookable-groups` + `.../booking-metadata`, shared with
 * the mobile bookings screen):
 *  - `booking-grouping`  — section/stay grouping + ordering + predicates
 *  - `BookingRow`        — the uniform per-item row
 *  - `SectionHeader`     — section title row
 *  - `HotelSection`      — a hotel stay (header + rows + bulk-remove dialog)
 */

import React from 'react';

import { calculateTotalsByCurrency, cn } from '@elara/client-core/lib/utils';

import { useItineraryState, useTripsState } from '#states/hooks.js';
import { EmptyBookingsIllustration } from '#ui/illustrations/empty-bookings.js';
import { CostTotals, EmptyState, Loader } from '#ui/index.js';

import { groupItemsByType, groupRoomsByStay, TYPE_GROUP_ICONS, TYPE_GROUP_LABELS } from './bookings/booking-grouping.js';
import { BookingRow } from './bookings/BookingRow.js';
import { EDGE_PADDING_CLASSES, ROW_LIST_CLASSES } from './bookings/constants.js';
import { HotelSection } from './bookings/HotelSection.js';
import { SectionHeader } from './bookings/SectionHeader.js';

export function BookingsView(): React.ReactNode {
  const tripsState = useTripsState();
  const itineraryState = useItineraryState();

  const currentTrip = tripsState.current;
  const items = itineraryState.items;
  const groups = groupItemsByType(items);

  if (!currentTrip) {
    return null;
  }

  if (!itineraryState.itemsReady) {
    return (
      <div className={cn('h-full w-full flex flex-col justify-center items-center p-6', EDGE_PADDING_CLASSES)}>
        <Loader loading={true} />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className={cn('h-full w-full flex flex-col justify-center items-center p-6', EDGE_PADDING_CLASSES)}>
        <EmptyState
          title="Bookings"
          description="Your trip bookings will appear here. Add flights, hotels, or activities to your itinerary to see them."
          illustration={<EmptyBookingsIllustration width={198} height={140} />}
        />
      </div>
    );
  }

  // Cancelled items no longer contribute to the trip's estimated cost.
  const totalCostsByCurrency = calculateTotalsByCurrency(items.filter((i) => !i.isCancelled));

  return (
    <div className="h-full w-full overflow-y-auto">
      <div className={cn('flex flex-col max-w-[1400px] mx-auto', EDGE_PADDING_CLASSES)}>
        <div className="flex flex-col w-full md:flex-row md:items-center gap-x-4 gap-y-4 py-8 @md:pb-10 justify-between">
          <div className="space-y-1">
            <h2 className="text-2xl font-display">Bookings</h2>
            <p className="text-sm text-muted-foreground">
              Pricing and availability is subject to change until confirmation.
            </p>
          </div>

          <div className="flex flex-col md:items-end gap-1">
            <span className="text-muted-foreground text-sm">Estimated total cost</span>
            <span className="text-xl font-medium">
              {totalCostsByCurrency && Object.keys(totalCostsByCurrency).length > 0 ? (
                <CostTotals
                  totalsByCurrency={totalCostsByCurrency}
                  className="flex flex-wrap md:justify-end items-baseline gap-1.5"
                />
              ) : (
                'N/A'
              )}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-8 pb-8 md:gap-12 md:pb-12">
          {groups.map(({ type, items: groupItems }) =>
            type === 'room' ? (
              groupRoomsByStay(groupItems).map((stay) => <HotelSection key={stay.key} items={stay.items} />)
            ) : (
              <section key={type}>
                <SectionHeader icon={TYPE_GROUP_ICONS[type]} title={TYPE_GROUP_LABELS[type]} />
                <div className={ROW_LIST_CLASSES}>
                  {groupItems.map((item) => (
                    <BookingRow key={item.id} item={item} />
                  ))}
                </div>
              </section>
            ),
          )}
        </div>
      </div>
    </div>
  );
}
